// scheduling/components/pool/PoolConfigEditor.jsx
// Admin editor for rprs.PoolConfig (Job Pools, Trello #257).
//
// Lives collapsed above the Pool Audit report because that's where an admin already
// goes to see whether the pools are behaving; tuning them from the same screen beats
// a separate tab. Mirrors the Recommendation Config pattern in AdminConsole: load
// current values, edit locally, save the whole set in one batch.
//
// Everything here used to require a deploy. Departments and minimum level in
// particular were hardcoded in DEFAULT_RULES, so "let Exp Tax take General Track too"
// was a code change.
//
// Two deliberate choices:
//   • Option lists (levels, departments, services) come from the SERVER, not from
//     client constants, so a new department or service can't leave the picklists
//     stale while the rules quietly accept it.
//   • Empty string is a MEANINGFUL value, not a blank to be coerced away: no
//     departments = any department, no minimum level = no floor. The pickers say so
//     in words rather than leaving an empty row.

import { useEffect, useMemo, useState } from 'react';
import { POOL_META, POOL_CODES } from '../../utils/poolEligibility';
import { fetchPoolConfig, updatePoolConfig } from '../../services/poolApi';

// Numeric thresholds, edited as plain numbers.
const THRESHOLDS = [
  { key: 'suggest_max_hours', label: 'Suggest pooling under', unit: 'h',
    help: 'Budget below which the Send to Pool button is visually promoted on a job row.' },
  { key: 'advisory_max_hours', label: 'Advisory warning at or above', unit: 'h',
    help: 'Budget at or above which the Send to Pool modal warns. Advisory only — it never blocks.' },
];

export default function PoolConfigEditor() {
  const [open, setOpen] = useState(false);       // collapsed by default
  const [cfg, setCfg] = useState(null);          // { key: value } as loaded
  const [draft, setDraft] = useState({});        // pending edits, same shape
  const [opts, setOpts] = useState({ levels: [], depts: [], services: [] });
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Load lazily — an admin who never expands this shouldn't pay for it.
  useEffect(() => {
    if (!open || cfg) return;
    setLoading(true);
    fetchPoolConfig()
      .then(r => {
        setCfg(r.config || {});
        setDraft(r.config || {});
        setOpts(r.options || { levels: [], depts: [], services: [] });
        setCounts(r.eligibleCounts || null);
      })
      .catch(e => setMsg({ kind: 'error', text: e.message }))
      .finally(() => setLoading(false));
  }, [open, cfg]);

  const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));
  const val = (key) => (draft[key] == null ? '' : String(draft[key]));

  // Only send what actually changed, so UpdatedBy/UpdatedAt stay meaningful.
  const dirtyKeys = useMemo(
    () => Object.keys(draft).filter(k => String(draft[k] ?? '') !== String((cfg || {})[k] ?? '')),
    [draft, cfg],
  );

  async function save() {
    if (!dirtyKeys.length) return;
    setSaving(true); setMsg(null);
    try {
      const res = await updatePoolConfig(dirtyKeys.map(k => ({ configKey: k, configValue: draft[k] })));
      setCfg(res.config || draft);
      setDraft(res.config || draft);
      setCounts(res.eligibleCounts || null);
      const unknown = (res.unknown || []).length ? ` (ignored unknown: ${res.unknown.join(', ')})` : '';
      setMsg({ kind: 'ok', text: `Saved ${res.applied?.length ?? dirtyKeys.length} setting(s). Applies immediately.${unknown}` });
      setTimeout(() => setMsg(null), 8000);
    } catch (e) {
      setMsg({ kind: 'error', text: e.message });
    } finally { setSaving(false); }
  }

  function reset() { setDraft(cfg || {}); setMsg(null); }

  // Multi-select as a checkbox row — a native <select multiple> is fiddly to use and
  // hides unselected options, which is exactly what an admin needs to see here.
  const DeptPicker = ({ code }) => {
    const key = `${code.toLowerCase()}.depts`;
    const chosen = val(key).split(',').map(s => s.trim()).filter(Boolean);
    const toggle = (d) => {
      const next = chosen.includes(d) ? chosen.filter(x => x !== d) : [...chosen, d];
      set(key, next.join(','));
    };
    return (
      <div className="sched-pool-cfg-depts">
        {opts.depts.map(d => (
          <label key={d} className={`sched-pool-cfg-chip${chosen.includes(d) ? ' sched-pool-cfg-chip--on' : ''}`}>
            <input type="checkbox" checked={chosen.includes(d)} onChange={() => toggle(d)} />
            {d}
          </label>
        ))}
        <span className="sched-pool-cfg-hint">
          {chosen.length ? `${chosen.length} selected` : 'none selected = ANY department (firm-wide)'}
        </span>
      </div>
    );
  };

  // Pilot allowlist: stores StaffIds, shows names. Superusers are always allowed and
  // are deliberately NOT listed here — they'd look removable when they aren't.
  const StaffPicker = () => {
    const key = 'access.allowed_staff';
    const chosen = val(key).split(',').map(s => s.trim()).filter(Boolean);
    const toggle = (id) => {
      const s = String(id);
      const next = chosen.includes(s) ? chosen.filter(x => x !== s) : [...chosen, s];
      set(key, next.join(','));
    };
    const [q, setQ] = useState('');
    const list = (opts.staff || []).filter(s =>
      !q || s.Name.toLowerCase().includes(q.toLowerCase()));
    const picked = (opts.staff || []).filter(s => chosen.includes(String(s.StaffId)));
    return (
      <div className="sched-pool-cfg-staffpick">
        {picked.length > 0 && (
          <div className="sched-pool-cfg-depts">
            {picked.map(s => (
              <label key={s.StaffId} className="sched-pool-cfg-chip sched-pool-cfg-chip--on">
                <input type="checkbox" checked onChange={() => toggle(s.StaffId)} />
                {s.Name}
              </label>
            ))}
          </div>
        )}
        <input
          type="text" className="sched-modal-job-search sched-pool-cfg-staffsearch"
          placeholder="Search staff to add…" value={q}
          onChange={e => setQ(e.target.value)}
        />
        {q && (
          <div className="sched-pool-cfg-staffresults">
            {list.slice(0, 12).map(s => (
              <button key={s.StaffId} type="button"
                className={`sched-pool-cfg-staffrow${chosen.includes(String(s.StaffId)) ? ' sched-pool-cfg-staffrow--on' : ''}`}
                onClick={() => toggle(s.StaffId)}>
                <span>{s.Name}</span>
                <span className="sched-pool-cfg-staffmeta">{s.Level} · {s.DepartmentCode}</span>
              </button>
            ))}
            {list.length === 0 && <div className="sched-pool-cfg-hint">No match.</div>}
          </div>
        )}
        <span className="sched-pool-cfg-hint">
          {picked.length ? `${picked.length} listed` : 'Nobody listed'} — plus all
          superusers, who always have access and can&rsquo;t be removed here.
        </span>
      </div>
    );
  };

  const servicesKey = 'poolable_services';
  const chosenServices = val(servicesKey).split(',').map(s => s.trim()).filter(Boolean);
  const toggleService = (s) => {
    const next = chosenServices.includes(s) ? chosenServices.filter(x => x !== s) : [...chosenServices, s];
    set(servicesKey, next.join(','));
  };

  return (
    <div className="sched-pool-cfg">
      <button type="button" className="sched-pool-cfg-toggle" onClick={() => setOpen(o => !o)}>
        <span className={`sched-pool-cfg-caret${open ? ' sched-pool-cfg-caret--open' : ''}`}>&#9656;</span>
        Pool configuration
        <span className="sched-pool-cfg-toggle-sub">
          eligibility, thresholds and which services can be pooled
        </span>
        {dirtyKeys.length > 0 && <span className="sched-pool-cfg-dirty">{dirtyKeys.length} unsaved</span>}
      </button>

      {open && (
        <div className="sched-pool-cfg-body">
          {loading && <div className="sched-pool-cfg-loading">Loading configuration…</div>}

          {!loading && cfg && (
            <>
              <p className="sched-pool-cfg-intro">
                Changes apply immediately — every claim and assignment is validated against
                these values server-side. Eligible headcounts update on save.
              </p>

              {/* ── Pilot access ── First, because during the pilot it's the setting
                  that decides whether anyone sees the feature at all. ── */}
              <fieldset className="sched-pool-cfg-pool">
                <legend>
                  Who can use Job Pools
                  {val('access.restricted') !== 'false' && (
                    <span className="sched-pool-cfg-pilot">PILOT</span>
                  )}
                </legend>

                <label className="sched-pool-cfg-row">
                  <span className="sched-pool-cfg-label">Access</span>
                  <select
                    value={val('access.restricted') === 'false' ? 'false' : 'true'}
                    onChange={e => set('access.restricted', e.target.value)}
                  >
                    <option value="true">Pilot group only</option>
                    <option value="false">Everyone (ends the pilot)</option>
                  </select>
                  <span className="sched-pool-cfg-hint">
                    Switching to Everyone ignores the list below entirely — normal
                    eligibility rules then apply firm-wide.
                  </span>
                </label>

                {val('access.restricted') !== 'false' && (
                  <label className="sched-pool-cfg-row">
                    <span className="sched-pool-cfg-label">Pilot members</span>
                    <StaffPicker />
                  </label>
                )}
              </fieldset>

              {/* ── Per-pool eligibility ── */}
              {POOL_CODES.map(code => {
                const meta = POOL_META[code] || {};
                const k = code.toLowerCase();
                return (
                  <fieldset key={code} className="sched-pool-cfg-pool">
                    <legend>
                      <span className="sched-pool-chip" style={{ background: meta.color || '#6B7280' }}>
                        {meta.label || code}
                      </span>
                      {counts && (
                        <span className="sched-pool-cfg-count">
                          {counts[code]} eligible {counts[code] === 1 ? 'staffer' : 'staff'}
                        </span>
                      )}
                    </legend>

                    <label className="sched-pool-cfg-row">
                      <span className="sched-pool-cfg-label">Departments</span>
                      <DeptPicker code={code} />
                    </label>

                    <label className="sched-pool-cfg-row">
                      <span className="sched-pool-cfg-label">Minimum level</span>
                      <select value={val(`${k}.min_level`)} onChange={e => set(`${k}.min_level`, e.target.value)}>
                        <option value="">No minimum</option>
                        {opts.levels.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </label>

                    <label className="sched-pool-cfg-row">
                      <span className="sched-pool-cfg-label">Leadership</span>
                      <select
                        value={val(`${k}.exclude_leadership`) === 'true' ? 'true' : 'false'}
                        onChange={e => set(`${k}.exclude_leadership`, e.target.value)}
                      >
                        <option value="true">Excluded</option>
                        <option value="false">Allowed</option>
                      </select>
                      <span className="sched-pool-cfg-hint">
                        Member, Principal, Senior Manager, Consultant
                      </span>
                    </label>

                    <label className="sched-pool-cfg-row">
                      <span className="sched-pool-cfg-label">Non-schedulable levels</span>
                      <select
                        value={val(`${k}.allow_nonschedulable`) === 'true' ? 'true' : 'false'}
                        onChange={e => set(`${k}.allow_nonschedulable`, e.target.value)}
                      >
                        <option value="false">Excluded</option>
                        <option value="true">Allowed</option>
                      </select>
                      <span className="sched-pool-cfg-hint">
                        Administrative, Admin Management, No Selection, Unknown, Former Employee
                      </span>
                    </label>
                  </fieldset>
                );
              })}

              {/* ── Which services may be pooled ── */}
              <fieldset className="sched-pool-cfg-pool">
                <legend>Poolable services</legend>
                <div className="sched-pool-cfg-depts">
                  {opts.services.map(s => (
                    <label key={s} className={`sched-pool-cfg-chip${chosenServices.includes(s) ? ' sched-pool-cfg-chip--on' : ''}`}>
                      <input type="checkbox" checked={chosenServices.includes(s)} onChange={() => toggleService(s)} />
                      {s}
                    </label>
                  ))}
                </div>
                <span className="sched-pool-cfg-hint">
                  Send to Pool is hidden for anything unticked, and rejected server-side.
                  Note PE must also have the pool work status mapped to that service, or
                  the send is refused with a config error.
                </span>
              </fieldset>

              {/* ── Thresholds ── */}
              <fieldset className="sched-pool-cfg-pool">
                <legend>Thresholds</legend>
                {THRESHOLDS.map(t => (
                  <label key={t.key} className="sched-pool-cfg-row">
                    <span className="sched-pool-cfg-label">{t.label}</span>
                    <input
                      type="number" min="0" step="1" className="sched-pool-cfg-num"
                      value={val(t.key)} onChange={e => set(t.key, e.target.value)}
                    />
                    <span className="sched-pool-cfg-unit">{t.unit}</span>
                    <span className="sched-pool-cfg-hint">{t.help}</span>
                  </label>
                ))}
              </fieldset>

              {/* PE status indices are intentionally NOT editable here: they must match
                  tblWorkStatus rows that a PE admin creates, so a free-text change would
                  just break sends. Shown read-only for reference. */}
              <fieldset className="sched-pool-cfg-pool">
                <legend>PE work statuses (read-only)</legend>
                <div className="sched-pool-cfg-readonly">
                  {['status_index.gen', 'status_index.exptax', 'status_index.etf', 'status_index.nfp', 'status_index.claimed']
                    .map(k => <span key={k}><code>{k}</code> = {val(k) || '—'}</span>)}
                </div>
                <span className="sched-pool-cfg-hint">
                  These point at PE <code>tblWorkStatus</code> rows and must be mapped to each
                  service in PE. Changing them needs a PE admin, so they aren't editable here.
                </span>
              </fieldset>

              <div className="sched-pool-cfg-actions">
                <button type="button" className="sched-assign-btn"
                  disabled={saving || !dirtyKeys.length} onClick={save}>
                  {saving ? 'Saving…' : dirtyKeys.length ? `Save ${dirtyKeys.length} change(s)` : 'No changes'}
                </button>
                <button type="button" className="sched-pool-sidebar-btn"
                  disabled={saving || !dirtyKeys.length} onClick={reset}>
                  Revert
                </button>
                {msg && (
                  <span className={msg.kind === 'ok' ? 'sched-pool-cfg-ok' : 'sched-pool-cfg-err'}>
                    {msg.text}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
